/**
 * File validation utilities for uploads
 * Shared constants and functions for validating file types and sizes
 */

// Allowed MIME types for resources and lesson plans
export const ALLOWED_FILE_TYPES = [
  // Documents
  'application/pdf', // .pdf
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  // Images
  'image/jpeg', // .jpg, .jpeg
  'image/png', // .png
  'image/gif', // .gif
  'image/webp', // .webp
  'image/bmp', // .bmp
  // Videos
  'video/mp4', // .mp4
  'video/avi', // .avi
  'video/quicktime', // .mov
  'video/x-msvideo', // .avi
  'video/webm', // .webm
  // Audio
  'audio/mpeg', // .mp3
  'audio/wav', // .wav
  'audio/flac', // .flac
  'audio/aac', // .aac
  'audio/ogg', // .ogg
  // Archives
  'application/zip', // .zip
  'application/x-rar-compressed', // .rar
  'application/x-7z-compressed', // .7z
  'application/x-tar', // .tar
  'application/gzip' // .gz
] as const;

// Allowed file extensions (fallback for browsers that don't report MIME types correctly)
export const ALLOWED_FILE_EXTENSIONS = [
  // Documents
  '.pdf', '.docx', '.doc', '.xlsx', '.xls',
  // Images
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp',
  // Videos
  '.mp4', '.avi', '.mov', '.wmv', '.webm',
  // Audio
  '.mp3', '.wav', '.flac', '.aac', '.ogg',
  // Archives
  '.zip', '.rar', '.7z', '.tar', '.gz'
] as const;

// Maximum file size in bytes (100MB)
export const MAX_FILE_SIZE = 100 * 1024 * 1024;

// Human-readable file size limit
export const MAX_FILE_SIZE_MB = 100;

// Error messages
export const FILE_VALIDATION_ERRORS = {
  INVALID_TYPE: 'Chỉ chấp nhận file: PDF, Word, Excel, Ảnh, Video, Audio, Archive (zip, rar, 7z, tar, gz)',
  EXCEEDS_SIZE: `Kích thước file không được vượt quá ${MAX_FILE_SIZE_MB}MB`,
  NO_FILE: 'Vui lòng chọn file'
} as const;

/**
 * Validate file type and size
 * @param file - File object to validate
 * @returns Validation result with isValid flag and optional error message
 */
export function validateFile(file: File): { isValid: boolean; error?: string } {
  if (!file) {
    return {
      isValid: false,
      error: FILE_VALIDATION_ERRORS.NO_FILE
    };
  }

  // Check file extension (fallback for browsers that don't report MIME types correctly)
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  
  if (!ALLOWED_FILE_TYPES.includes(file.type as any) && !ALLOWED_FILE_EXTENSIONS.includes(fileExtension as any)) {
    return {
      isValid: false,
      error: FILE_VALIDATION_ERRORS.INVALID_TYPE
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: FILE_VALIDATION_ERRORS.EXCEEDS_SIZE
    };
  }

  return { isValid: true };
}

/**
 * Get file extension from filename
 * @param fileName - File name
 * @returns File extension with dot (e.g., '.pdf')
 */
export function getFileExtension(fileName: string): string {
  return fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
}

/**
 * Check if file type is allowed
 * @param file - File object
 * @returns True if file type is allowed
 */
export function isFileTypeAllowed(file: File): boolean {
  const fileExtension = getFileExtension(file.name);
  return ALLOWED_FILE_TYPES.includes(file.type as any) || ALLOWED_FILE_EXTENSIONS.includes(fileExtension as any);
}

/**
 * Get react-dropzone accept configuration
 * @returns Accept object for useDropzone hook
 */
export function getDropzoneAcceptConfig(): Record<string, string[]> {
  return {
    // Documents
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    // Images
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
    'image/bmp': ['.bmp'],
    // Videos
    'video/mp4': ['.mp4'],
    'video/avi': ['.avi'],
    'video/quicktime': ['.mov'],
    'video/x-msvideo': ['.avi'],
    'video/webm': ['.webm'],
    // Audio
    'audio/mpeg': ['.mp3'],
    'audio/wav': ['.wav'],
    'audio/flac': ['.flac'],
    'audio/aac': ['.aac'],
    'audio/ogg': ['.ogg'],
    // Archives
    'application/zip': ['.zip'],
    'application/x-rar-compressed': ['.rar'],
    'application/x-7z-compressed': ['.7z'],
    'application/x-tar': ['.tar'],
    'application/gzip': ['.gz']
  };
}

/**
 * Get accept string for HTML input element
 * @returns Comma-separated list of file extensions
 */
export function getInputAcceptString(): string {
  return ALLOWED_FILE_EXTENSIONS.join(',');
}

/**
 * Format file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

